import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Shield,
  ShieldCheck,
  ShieldX,
  ShieldAlert,
  Clock,
  CheckCircle2,
  XCircle,
  ExternalLink,
  RefreshCw,
  FileCheck,
  Camera,
  IdCard,
  AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { verificationApi, VerificationStatus } from '../../lib/api';
import { GlassButton, PageLoader } from '../../components/ui';

export default function VerificationPage() {
  const [status, setStatus] = useState<VerificationStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [serviceStatus, setServiceStatus] = useState<{ diditConfigured: boolean } | null>(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setIsLoading(true);
      const [statusRes, serviceRes] = await Promise.all([
        verificationApi.check(),
        verificationApi.getStatus(),
      ]);
      
      if (statusRes.data.success && statusRes.data.data) {
        setStatus(statusRes.data.data);
      }
      if (serviceRes.data.success && serviceRes.data.data) {
        setServiceStatus(serviceRes.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch verification status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartVerification = async () => {
    try {
      setIsStarting(true);
      const response = await verificationApi.start({
        redirectUrl: `${window.location.origin}/professor?verified=true`,
        failureUrl: `${window.location.origin}/professor/verification?failed=true`,
      });

      if (response.data.success && response.data.data?.verificationUrl) {
        // Open verification URL in new window
        window.location.href = response.data.data.verificationUrl;
      } else {
        toast.error('Failed to start verification');
      }
    } catch (error) {
      console.error('Failed to start verification:', error);
      toast.error('Failed to start verification');
    } finally {
      setIsStarting(false);
    }
  };

  const handleResumeVerification = async () => {
    try {
      setIsResuming(true);
      const response = await verificationApi.resume({
        redirectUrl: `${window.location.origin}/professor?verified=true`,
        failureUrl: `${window.location.origin}/professor/verification?failed=true`,
      });

      if (response.data.success && response.data.data) {
        if (response.data.data.alreadyVerified) {
          toast.success('Your identity is already verified!');
          fetchStatus();
          return;
        }
        
        if (response.data.data.verificationUrl) {
          // Redirect to verification URL (either resumed or new session)
          window.location.href = response.data.data.verificationUrl;
        } else {
          toast.error('Failed to resume verification');
        }
      } else {
        toast.error('Failed to resume verification');
      }
    } catch (error) {
      console.error('Failed to resume verification:', error);
      toast.error('Failed to resume verification');
    } finally {
      setIsResuming(false);
    }
  };

  const getStatusIcon = () => {
    switch (status?.verificationStatus) {
      case 'VERIFIED':
        return <ShieldCheck className="w-16 h-16 text-green-500" />;
      case 'PENDING':
        return <Clock className="w-16 h-16 text-yellow-500" />;
      case 'FAILED':
      case 'EXPIRED':
        return <ShieldX className="w-16 h-16 text-red-500" />;
      default:
        return <ShieldAlert className="w-16 h-16 text-purple-500" />;
    }
  };

  const getStatusColor = () => {
    switch (status?.verificationStatus) {
      case 'VERIFIED':
        return 'from-green-500/20 to-emerald-500/20 border-green-500/30';
      case 'PENDING':
        return 'from-yellow-500/20 to-amber-500/20 border-yellow-500/30';
      case 'FAILED':
      case 'EXPIRED':
        return 'from-red-500/20 to-rose-500/20 border-red-500/30';
      default:
        return 'from-purple-500/20 to-violet-500/20 border-purple-500/30';
    }
  };

  const getStatusMessage = () => {
    switch (status?.verificationStatus) {
      case 'VERIFIED':
        return 'Your identity has been verified. You can now submit courses for review and publish them.';
      case 'PENDING':
        return 'Your verification was started but not completed. Click "Resume Verification" to continue where you left off.';
      case 'FAILED':
        return 'Your verification was not successful. Please try again with a valid government ID.';
      case 'EXPIRED':
        return 'Your verification session expired. Please start a new verification.';
      default:
        return 'Verify your identity to start publishing courses on Ulearning.';
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center p-4 rounded-2xl bg-gradient-to-br from-purple-500/20 to-violet-500/20 mb-6">
            <Shield className="w-12 h-12 text-purple-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">Identity Verification</h1>
          <p className="text-gray-400 max-w-lg mx-auto">
            Verify your identity to become a trusted professor on Ulearning. 
            This helps us maintain a safe and professional learning environment.
          </p>
        </motion.div>

        {/* Status Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`bg-gradient-to-br ${getStatusColor()} border rounded-2xl p-8 mb-8`}
        >
          <div className="flex flex-col items-center text-center">
            {getStatusIcon()}
            <h2 className="text-xl font-semibold text-white mt-4 mb-2">
              {status?.verificationStatus === 'VERIFIED' 
                ? 'Verified Professor' 
                : status?.verificationStatus === 'PENDING'
                ? 'Verification In Progress'
                : status?.verificationStatus === 'FAILED'
                ? 'Verification Failed'
                : status?.verificationStatus === 'EXPIRED'
                ? 'Verification Expired'
                : 'Not Verified'
              }
            </h2>
            <p className="text-gray-300 mb-6">{getStatusMessage()}</p>
            
            {status?.verifiedAt && (
              <p className="text-sm text-gray-400">
                Verified on: {new Date(status.verifiedAt).toLocaleDateString()}
              </p>
            )}

            {/* Action Buttons */}
            {status?.verificationStatus !== 'VERIFIED' && status?.verificationStatus !== 'PENDING' && (
              <GlassButton
                onClick={handleStartVerification}
                disabled={isStarting || !serviceStatus?.diditConfigured}
                className="mt-6"
                icon={isStarting ? RefreshCw : ExternalLink}
              >
                {isStarting ? 'Starting...' : 'Start Verification'}
              </GlassButton>
            )}

            {status?.verificationStatus === 'PENDING' && (
              <div className="flex flex-col items-center gap-4 mt-6">
                <GlassButton
                  onClick={handleResumeVerification}
                  disabled={isResuming || !serviceStatus?.diditConfigured}
                  icon={isResuming ? RefreshCw : ExternalLink}
                >
                  {isResuming ? 'Loading...' : 'Resume Verification'}
                </GlassButton>
                <button
                  onClick={fetchStatus}
                  className="text-gray-400 hover:text-gray-300 flex items-center gap-2 text-sm"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh Status
                </button>
              </div>
            )}
          </div>
        </motion.div>

        {/* Process Steps */}
        {status?.verificationStatus !== 'VERIFIED' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#111111] border border-gray-800 rounded-2xl p-8"
          >
            <h3 className="text-lg font-semibold text-white mb-6">Verification Process</h3>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-violet-500/20 flex items-center justify-center">
                  <IdCard className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h4 className="font-medium text-white">1. Government ID</h4>
                  <p className="text-sm text-gray-400 mt-1">
                    Take a clear photo of your government-issued ID (passport, driver's license, or national ID).
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-violet-500/20 flex items-center justify-center">
                  <Camera className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h4 className="font-medium text-white">2. Selfie Verification</h4>
                  <p className="text-sm text-gray-400 mt-1">
                    Take a live selfie to confirm your identity matches your ID photo.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-violet-500/20 flex items-center justify-center">
                  <FileCheck className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h4 className="font-medium text-white">3. Automatic Review</h4>
                  <p className="text-sm text-gray-400 mt-1">
                    Our system verifies your documents automatically. This usually takes just a few minutes.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl">
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-yellow-400">Privacy Note</h4>
                  <p className="text-sm text-yellow-300/70 mt-1">
                    Your ID is processed securely and we only store the verification result, not your actual documents.
                    Your personal information is protected under our Privacy Policy.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Benefits for Verified Professors */}
        {status?.verificationStatus === 'VERIFIED' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#111111] border border-gray-800 rounded-2xl p-8"
          >
            <h3 className="text-lg font-semibold text-white mb-6">Verification Benefits</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-4 bg-[#1a1a1a] rounded-xl">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span className="text-gray-300">Submit courses for review</span>
              </div>
              <div className="flex items-center gap-3 p-4 bg-[#1a1a1a] rounded-xl">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span className="text-gray-300">Publish approved courses</span>
              </div>
              <div className="flex items-center gap-3 p-4 bg-[#1a1a1a] rounded-xl">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span className="text-gray-300">Verified badge on profile</span>
              </div>
              <div className="flex items-center gap-3 p-4 bg-[#1a1a1a] rounded-xl">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span className="text-gray-300">Accept student payments</span>
              </div>
            </div>
          </motion.div>
        )}

        {/* Service Not Configured Warning */}
        {serviceStatus && !serviceStatus.diditConfigured && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-8 p-4 bg-red-500/10 border border-red-500/20 rounded-xl"
          >
            <div className="flex gap-3">
              <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-red-400">Verification Unavailable</h4>
                <p className="text-sm text-red-300/70 mt-1">
                  Identity verification is currently unavailable. Please try again later or contact support.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
